import { useState, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { clsx } from 'clsx'
import { useNavigate } from '@tanstack/react-router'
import { OptionCard } from './OptionCard'
import { CreationSuccessOverlay } from './CreationSuccessOverlay'
import { ScoringRulesSheet } from './ScoringRulesSheet'
import { useLeagueCreation } from '../hooks/useLeagueCreation'
import { useCompetitions } from '../hooks/useCompetitions'
import { LeagueFormat, LeagueTransferOption, LeagueScoringSystem } from '../types'
import styles from './LeagueCreationForm.module.css'

const MAX_NAME_LENGTH = 40

const STEP_HEADINGS = ['Nome da Liga', 'Competição', 'Formato']

function competitionIcon(sport: string): string {
  return sport === 'FOOTBALL' ? '⚽' : '🖥️'
}

export function LeagueCreationForm() {
  const [step, setStep] = useState(1)
  const [showRules, setShowRules] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!isTransitioning) return
    const id = requestAnimationFrame(() => setIsTransitioning(false))
    return () => cancelAnimationFrame(id)
  }, [isTransitioning])

  const {
    register,
    control,
    handleSubmit,
    watch,
    trigger,
    errors,
    formError,
    isCreating,
    onSubmit,
  } = useLeagueCreation()

  const { data: competitions, isPending: competitionsPending } = useCompetitions()

  const nameLength = watch('name')?.length ?? 0

  async function goNext() {
    let valid = false
    if (step === 1) valid = await trigger(['name', 'teamName'])
    else if (step === 2) valid = await trigger(['competitionId', 'scoringSystem'])
    if (valid && step < 3) {
      setIsTransitioning(true)
      setStep(step + 1)
    }
  }

  function goBack() {
    if (step > 1) setStep(step - 1)
  }

  function handleCancel() {
    void navigate({ to: '/ligas' })
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageBg} aria-hidden="true" />

      <form
        className={styles.wizardWrap}
        onSubmit={(e) => e.preventDefault()}
        noValidate
      >
        {/* Step indicator */}
        <div className={styles.stepIndicator} role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={3} aria-label={`Passo ${step} de 3`}>
          <div className={clsx(styles.stepDot, styles.stepDotActive)} />
          <div className={clsx(styles.stepLine, step > 1 && styles.stepLineActive)} />
          <div className={clsx(styles.stepDot, step >= 2 && styles.stepDotActive)} />
          <div className={clsx(styles.stepLine, step > 2 && styles.stepLineActive)} />
          <div className={clsx(styles.stepDot, step >= 3 && styles.stepDotActive)} />
        </div>

        {/* Wizard card */}
        <div className={styles.wizardCard}>
          {/* Header */}
          <div className={styles.cardHeader}>
            <span className={styles.stepLabel}>PASSO {step} DE 3</span>
            <h1 className={styles.cardHeading}>{STEP_HEADINGS[step - 1]}</h1>
          </div>

          {/* Body */}
          <div className={styles.cardBody}>

            {/* ── STEP 1: Nome da Liga ─────────────────────────── */}
            {step === 1 && (
              <>
                <label htmlFor="league-name" className={styles.sectionLabel}>
                  NOME DA LIGA
                </label>
                <input
                  id="league-name"
                  className={clsx(styles.nameInput, errors.name && styles.nameInputError)}
                  type="text"
                  placeholder="Ex: Os Incompetentes FC"
                  maxLength={MAX_NAME_LENGTH}
                  autoComplete="off"
                  autoFocus
                  disabled={isCreating}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  aria-invalid={!!errors.name}
                  {...register('name')}
                />
                <div className={styles.nameFooterRow}>
                  {errors.name ? (
                    <p className={styles.fieldError} id="name-error" role="alert">
                      {errors.name.message}
                    </p>
                  ) : (
                    <span />
                  )}
                  <span
                    className={clsx(
                      styles.nameCounter,
                      nameLength >= MAX_NAME_LENGTH - 5 && styles.nameCounterWarn,
                      nameLength >= MAX_NAME_LENGTH && styles.nameCounterMax,
                    )}
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {nameLength} / {MAX_NAME_LENGTH}
                  </span>
                </div>

                <label htmlFor="team-name" className={styles.sectionLabel}>
                  NOME DO SEU TIME (OPCIONAL)
                </label>
                <input
                  id="team-name"
                  className={clsx(styles.nameInput, errors.teamName && styles.nameInputError)}
                  type="text"
                  placeholder="Ex: Fantyx FC"
                  maxLength={30}
                  autoComplete="off"
                  disabled={isCreating}
                  aria-describedby={errors.teamName ? 'team-name-error' : undefined}
                  aria-invalid={!!errors.teamName}
                  {...register('teamName')}
                />
                {errors.teamName && (
                  <p className={styles.fieldError} id="team-name-error" role="alert">
                    {errors.teamName.message}
                  </p>
                )}

                <div className={styles.infoNote}>
                  <span className={styles.infoNoteIcon} aria-hidden="true">ℹ</span>
                  <p className={styles.infoNoteText}>
                    Configurações avançadas e número máximo de participantes podem ser
                    ajustados antes da primeira rodada.
                  </p>
                </div>
              </>
            )}

            {/* ── STEP 2: Competição + Sistema de Pontuação ─────── */}
            {step === 2 && (
              <>
                <section className={styles.section}>
                  <p className={styles.sectionLabel}>COMPETIÇÃO</p>
                  <Controller
                    name="competitionId"
                    control={control}
                    render={({ field }) => (
                      <div role="radiogroup" aria-label="Competição" className={styles.optionGrid2}>
                        {competitionsPending ? (
                          <div className={styles.competitionSkeleton} aria-hidden="true" />
                        ) : (
                          competitions?.map((comp) =>
                            comp.isActive ? (
                              <OptionCard
                                key={comp.id}
                                id={`competition-${comp.id}`}
                                value={comp.id}
                                title={comp.name}
                                icon={competitionIcon(comp.sport)}
                                eyebrow="DISPONÍVEL"
                                description="Competição disponível para nova liga."
                                isSelected={field.value === comp.id}
                                onChange={field.onChange}
                              />
                            ) : (
                              <OptionCard
                                key={comp.id}
                                id={`competition-${comp.id}`}
                                value={comp.id}
                                title={comp.name}
                                icon={competitionIcon(comp.sport)}
                                eyebrow="EM BREVE"
                                description="Em breve disponível."
                                isSelected={false}
                                isLocked
                              />
                            ),
                          )
                        )}
                      </div>
                    )}
                  />
                  {errors.competitionId && (
                    <p className={styles.fieldError} role="alert">
                      {errors.competitionId.message}
                    </p>
                  )}
                </section>

                <section className={styles.section}>
                  <div className={styles.labelRow}>
                    <p className={styles.sectionLabel}>SISTEMA DE PONTUAÇÃO</p>
                    <button
                      type="button"
                      className={styles.rulesLink}
                      onClick={() => setShowRules(true)}
                    >
                      Ver regras
                    </button>
                  </div>
                  <Controller
                    name="scoringSystem"
                    control={control}
                    render={({ field }) => (
                      <div
                        role="radiogroup"
                        aria-label="Sistema de pontuação"
                        className={styles.optionGrid2}
                      >
                        <OptionCard
                          id="scoring-nossa"
                          value={LeagueScoringSystem.NossaPreset}
                          title="Nossa Preset"
                          icon="⚡"
                          eyebrow="PADRÃO"
                          description="Regras equilibradas e balanceadas para a competição."
                          isSelected={field.value === LeagueScoringSystem.NossaPreset}
                          onChange={field.onChange}
                        />
                        <OptionCard
                          id="scoring-personalizado"
                          value="PERSONALIZADO"
                          title="Personalizado"
                          icon="⚙️"
                          eyebrow="EM BREVE"
                          description="Configure suas próprias regras de pontuação."
                          isSelected={false}
                          isLocked
                        />
                      </div>
                    )}
                  />
                </section>
              </>
            )}

            {/* ── STEP 3: Formato + Transferências ─────────────── */}
            {step === 3 && (
              <>
                <section className={styles.section}>
                  <p className={styles.sectionLabel}>FORMATO</p>
                  <Controller
                    name="format"
                    control={control}
                    render={({ field }) => (
                      <div
                        role="radiogroup"
                        aria-label="Formato da liga"
                        className={styles.optionGrid2}
                      >
                        <OptionCard
                          id="format-overall"
                          value={LeagueFormat.OverallPoints}
                          title="Pontuação Geral"
                          icon="🏆"
                          eyebrow="CLÁSSICO"
                          description="Acumule pontos ao longo de toda a competição."
                          isSelected={field.value === LeagueFormat.OverallPoints}
                          onChange={field.onChange}
                        />
                        <OptionCard
                          id="format-h2h"
                          value={LeagueFormat.HeadToHead}
                          title="Frente a Frente"
                          icon="⚔️"
                          eyebrow="CONFRONTO"
                          description="Enfrente um adversário diferente por rodada."
                          isSelected={field.value === LeagueFormat.HeadToHead}
                          onChange={field.onChange}
                        />
                        <OptionCard
                          id="format-draft"
                          value="DRAFT"
                          title="Draft"
                          icon="🎲"
                          eyebrow="EM BREVE"
                          description="Selecione jogadores em turnos com os rivais."
                          isSelected={false}
                          isLocked
                        />
                        <OptionCard
                          id="format-personalizado"
                          value="PERSONALIZADO"
                          title="Personalizado"
                          icon="⚙️"
                          eyebrow="EM BREVE"
                          description="Configure o formato da liga."
                          isSelected={false}
                          isLocked
                        />
                      </div>
                    )}
                  />
                </section>

                <section className={styles.section}>
                  <p className={styles.sectionLabel}>TRANSFERÊNCIAS</p>
                  <Controller
                    name="transferOption"
                    control={control}
                    render={({ field }) => (
                      <div
                        role="radiogroup"
                        aria-label="Regras de transferência"
                        className={styles.optionGrid2}
                      >
                        <OptionCard
                          id="transfer-optionb"
                          value={LeagueTransferOption.OptionB}
                          title="Ilimitadas"
                          icon="∞"
                          eyebrow="PADRÃO"
                          description="Transferências livres e ilimitadas por rodada."
                          isSelected={field.value === LeagueTransferOption.OptionB}
                          onChange={field.onChange}
                        />
                        <OptionCard
                          id="transfer-optiona"
                          value={LeagueTransferOption.OptionA}
                          title="3 por Rodada"
                          icon="🔄"
                          eyebrow="COM LIMITE"
                          description="3 livres por rodada. Extras: −5 pts cada."
                          isSelected={field.value === LeagueTransferOption.OptionA}
                          onChange={field.onChange}
                        />
                      </div>
                    )}
                  />
                </section>

                <div className={styles.infoNote}>
                  <span className={styles.infoNoteIcon} aria-hidden="true">ℹ</span>
                  <p className={styles.infoNoteText}>
                    Configurações avançadas e número máximo de participantes podem ser
                    ajustados antes da primeira rodada.
                  </p>
                </div>

                {formError && (
                  <div className={styles.formErrorAlert} role="alert">
                    <p>{formError}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer nav */}
          <div className={styles.cardFooter}>
            <button
              type="button"
              className={styles.btnBack}
              onClick={step === 1 ? handleCancel : goBack}
              disabled={isCreating}
            >
              {step === 1 ? 'Cancelar' : '← Voltar'}
            </button>

            <button
              type="button"
              className={styles.btnNext}
              disabled={isCreating || isTransitioning || (step === 3 && !!formError)}
              onClick={step < 3 ? goNext : () => void handleSubmit(onSubmit)()}
            >
              {step === 3
                ? (isCreating ? 'Criando…' : 'Criar Liga →')
                : 'Próximo →'}
            </button>
          </div>
        </div>
      </form>

      {isCreating && <CreationSuccessOverlay />}
      {showRules && <ScoringRulesSheet onClose={() => setShowRules(false)} />}
    </div>
  )
}
